import { createFileRoute } from "@tanstack/react-router";
import { exteriorPaletteLibraryAsync } from "building-generation/utils/builtinPaletteLoader";
import { WEB_API_BASE_PATH } from "common/constants";
import { exteriorPaletteIds } from "common/constants/palette/builtInPaletteIndex";
import { fetchWithTimeout } from "common/utils/fetchHelpers";
import { getToken } from "lib/ctx";
import getPageTitle from "lib/utils/getPageTitle";
import type { JSX } from "react";
import { useState, useCallback } from "react";
import { Helmet as Head } from "react-helmet-async";
import Button from "ui/buttons/Button";
import Input from "ui/forms/Input";
import Select from "ui/forms/Select";
import withAuthentication from "../../components/withAuthentication";
import styles from "./webgl-service.module.scss";

interface WebGLServiceParams {
  paletteId: string;
  fileFormat: "image/jpeg" | "image/png";
  paletteLayout: "square" | "rect";
  serviceUrl: string;
}

const formatOptions = [
  { label: "PNG", value: "image/png" },
  { label: "JPEG", value: "image/jpeg" },
];

const layoutOptions = [
  { label: "Square", value: "square" },
  { label: "Rectangle", value: "rect" },
];

// Currently the cold start time can be over 15 seconds, so we need to allow for that.
const REQUEST_TIMEOUT_SECONDS = 30;

// Sample palette IDs from the exterior palette library
const samplePaletteIds = exteriorPaletteIds;

function WebGLServicePage(): JSX.Element {
  const [params, setParams] = useState<WebGLServiceParams>({
    paletteId: samplePaletteIds[0],
    fileFormat: "image/png",
    paletteLayout: "square",
    serviceUrl: `${WEB_API_BASE_PATH}/webgl`,
  });

  const [loading, setLoading] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>("");
  const [error, setError] = useState<string>("");
  const [responseTime, setResponseTime] = useState<number | null>(null);

  const handleParamChange = useCallback(
    (key: keyof WebGLServiceParams) => (value: string | number | boolean | null) => {
      setParams((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    [],
  );

  const generateThumbnail = useCallback(async () => {
    if (!params.paletteId.trim()) {
      setError("Palette ID is required");
      return;
    }

    setLoading(true);
    setError("");
    setThumbnailUrl("");
    setResponseTime(null);

    const startTime = Date.now();

    try {
      const response = await fetchWithTimeout(
        `${params.serviceUrl}/generate-palette-thumbnail`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            authorization: (await getToken()) ?? "",
          },
          body: JSON.stringify({
            paletteId: params.paletteId,
            fileFormat: params.fileFormat,
            paletteLayout: params.paletteLayout,
          }),
        },
        REQUEST_TIMEOUT_SECONDS * 1000,
      );

      const endTime = Date.now();
      setResponseTime(endTime - startTime);

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      // Parse the JSON response to get the image URL
      const result = await response.json();

      if (result.imageUrl) {
        setThumbnailUrl(result.imageUrl);
      } else {
        throw new Error("No image URL returned from service");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unknown error occurred: " + err);
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [params]);

  const checkServiceHealth = useCallback(async () => {
    try {
      const response = await fetch(`${params.serviceUrl}/health`, {
        headers: {
          "Content-Type": "application/json",
          authorization: (await getToken()) ?? "",
        },
      });
      if (response.ok) {
        const data = await response.json();
        alert(`Service Status: ${data.status}\nInitialized: ${data.initialized}`);
      } else {
        alert(`Service health check failed: ${response.status}`);
      }
    } catch (err) {
      alert(`Service health check failed: ${err instanceof Error ? err.message : "Unknown error"}`);
    }
  }, [params.serviceUrl]);

  const clearThumbnail = useCallback(() => {
    setThumbnailUrl("");
    setError("");
    setResponseTime(null);
  }, []);

  const downloadThumbnail = useCallback(() => {
    if (!thumbnailUrl) {
      return;
    }

    const link = document.createElement("a");
    link.href = thumbnailUrl;
    link.download = `palette-${params.paletteId}.${params.fileFormat.split("/")[1]}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [thumbnailUrl, params.paletteId, params.fileFormat]);

  const availablePaletteIds = exteriorPaletteLibraryAsync.ids.slice(0, 50); // Limit for dropdown

  return (
    <>
      <Head>
        <title>{getPageTitle("WebGL Service Test")}</title>
      </Head>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>WebGL Palette Thumbnail Service Test</h1>
          <p>Test the WebGL service for generating exterior palette thumbnails</p>
        </div>

        <div className={styles.controls}>
          <div className={styles.inputGroup}>
            <label htmlFor="serviceUrl">Service URL:</label>
            <Input
              id="serviceUrl"
              type="text"
              value={params.serviceUrl}
              onChange={(e) => handleParamChange("serviceUrl")(e.currentTarget.value)}
              placeholder="http://localhost:3001"
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="paletteId">Palette ID:</label>
            <Select
              value={params.paletteId}
              onChange={handleParamChange("paletteId")}
              options={[
                ...samplePaletteIds.map((id) => ({ label: id, value: id })),
                ...availablePaletteIds
                  .filter((id) => !samplePaletteIds.includes(id))
                  .map((id) => ({ label: id, value: id })),
              ]}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="fileFormat">File Format:</label>
            <Select value={params.fileFormat} onChange={handleParamChange("fileFormat")} options={formatOptions} />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="paletteLayout">Layout:</label>
            <Select
              value={params.paletteLayout}
              onChange={handleParamChange("paletteLayout")}
              options={layoutOptions}
            />
          </div>

          <div className={styles.buttonGroup}>
            <Button onClick={generateThumbnail} disabled={loading}>
              {loading ? "Generating..." : "Generate Thumbnail"}
            </Button>
            <Button onClick={checkServiceHealth}>Check Service Health</Button>
            <Button onClick={clearThumbnail} disabled={!thumbnailUrl && !error}>
              Clear
            </Button>
          </div>
        </div>

        {error && (
          <div className={styles.error}>
            <h3>Error:</h3>
            <pre>{error}</pre>
          </div>
        )}

        {responseTime !== null && <div className={styles.responseTime}>Response time: {responseTime}ms</div>}

        {thumbnailUrl && (
          <div className={styles.result}>
            <div className={styles.imageContainer}>
              <img src={thumbnailUrl} alt="Generated palette thumbnail" className={styles.thumbnail} />
            </div>
            <div className={styles.imageInfo}>
              <p>
                <strong>Palette:</strong> {params.paletteId}
              </p>
              <p>
                <strong>Format:</strong> {params.fileFormat}
              </p>
              <p>
                <strong>Layout:</strong> {params.paletteLayout}
              </p>
              <Button onClick={downloadThumbnail}>Download Thumbnail</Button>
            </div>
          </div>
        )}

        <div className={styles.info}>
          <h3>Instructions:</h3>
          <ol>
            <li>Ensure the WebGL service is running on the specified URL</li>
            <li>Select a palette ID from the dropdown or enter a custom one</li>
            <li>Choose the desired file format and layout</li>
            <li>Click &quot;Generate Thumbnail&quot; to create the palette thumbnail</li>
            <li>Use &quot;Check Service Health&quot; to verify the service is running properly</li>
          </ol>

          <h3>Service Endpoints:</h3>
          <ul>
            <li>
              <code>GET /health</code> - Service health check
            </li>
            <li>
              <code>POST /generate-palette-thumbnail</code> - Generate palette thumbnail
            </li>
          </ul>

          <h3>Request Format:</h3>
          <pre>{`{
  "paletteId": "string",
  "fileFormat": "image/png" | "image/jpeg",
  "paletteLayout": "square" | "rect"
}`}</pre>

          <h3>Response Format:</h3>
          <pre>{`{
  "imageUrl": "string"
}`}</pre>
        </div>
      </div>
    </>
  );
}

export const Route = createFileRoute("/debug/webgl-service")({
  component: withAuthentication(WebGLServicePage, { superAdmin: true }),
});
